import React, { useEffect, useState, useMemo } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

type Lead = {
  id: string;
  name: string;
  email: string;
  phone: string;
  interest: string;
  source: string;
  date: string;
  assignedTo: string | null;
};

type Counselor = {
  id: number;
  name: string;
  email: string;
};

const LeadEnquiry: React.FC = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [counselors, setCounselors] = useState<Counselor[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const adminAuthData = JSON.parse(localStorage.getItem("mediit_admin_auth") || "{}");
  const userRole = adminAuthData.role || "Super Admin";
  const userEmail = adminAuthData.email || "";
  const userName = adminAuthData.name || "Admin";

  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [courseFilter, setCourseFilter] = useState("All");
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);

  // Modals State
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedCounselorEmail, setSelectedCounselorEmail] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);

  // Remark Modal State
  const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
  const [currentLeadForRemark, setCurrentLeadForRemark] = useState<Lead | null>(null);
  const [remarkData, setRemarkData] = useState({ status: "Interested", text: "" });
  const [isSubmittingRemark, setIsSubmittingRemark] = useState(false);

  useEffect(() => {
    fetchLeads();
    if (userRole === "Super Admin") fetchCounselors();
  }, []);

  const fetchLeads = async () => {
    try {
      // const response = await fetch(`http://localhost:9090/api/enquiry/all?role=${userRole}&email=${userEmail}`);
      const response = await fetch(`https://node-api.mediitacademy.com/api/enquiry/all?role=${userRole}&email=${userEmail}`);
      const data = await response.json();
      if (data.success) setLeads(data.leads);
    } catch (error) {
      console.error("Failed to fetch leads", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCounselors = async () => {
    try {
      // const response = await fetch("http://localhost:9090/api/enquiry/counselors");
      const response = await fetch("https://node-api.mediitacademy.com/api/enquiry/counselors");
      const data = await response.json();
      if (data.success) setCounselors(data.counselors);
    } catch (error) {
      console.error("Failed to fetch counselors", error);
    }
  };

  const handleAssignLeads = async () => {
    if (!selectedCounselorEmail) return alert("Select counselor first!");
    setIsAssigning(true);
    try {
      // const response = await fetch("http://localhost:9090/api/enquiry/assign", {
      const response = await fetch("https://node-api.mediitacademy.com/api/enquiry/assign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadIds: selectedLeads, counselorEmail: selectedCounselorEmail, adminName: userName }),
      });
      const data = await response.json();
      if (data.success) {
        alert("Assigned successfully!");
        setIsAssignModalOpen(false);
        setSelectedLeads([]);
        setSelectedCounselorEmail("");
        fetchLeads();
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleAddRemark = async () => {
    if (!remarkData.text.trim()) return alert("Remark cannot be empty!");
    setIsSubmittingRemark(true);
    try {
      // const response = await fetch("http://localhost:9090/api/enquiry/add-remark", {
      const response = await fetch("https://node-api.mediitacademy.com/api/enquiry/add-remark", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leadId: currentLeadForRemark?.id,
          counselorEmail: userEmail,
          counselorName: userName,
          status: remarkData.status,
          remark: remarkData.text
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert("Remark added!");
        setIsRemarkModalOpen(false);
        setRemarkData({ status: "Interested", text: "" });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setIsSubmittingRemark(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const uniqueCourses = useMemo(() => ["All", ...Array.from(new Set(leads.map(l => l.interest)))], [leads]);
  const filteredLeads = useMemo(() => leads.filter(l => (l.name.toLowerCase().includes(searchTerm.toLowerCase()) || l.phone.includes(searchTerm)) && (sourceFilter === "All" || l.source === sourceFilter) && (courseFilter === "All" || l.interest === courseFilter)), [leads, searchTerm, sourceFilter, courseFilter]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => setSelectedLeads(e.target.checked ? filteredLeads.map(l => l.id) : []);
  const handleSelectLead = (id: string) => setSelectedLeads(prev => prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]);

  const shortenEmail = (email: string | null) => email ? email.split('@')[0] : "";

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-black text-slate-900">Lead & Enquiry Management</h1>
        <p className="text-sm font-semibold text-slate-500">{userRole === "Super Admin" ? "Track, filter, and assign enquiries." : "Manage assigned leads & update status."}</p>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
        <div className="lg:col-span-2">
          <input type="text" placeholder="Search name or phone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold outline-none" />
        </div>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="w-full rounded-2xl border border-slate-200 px-5 py-3 text-sm font-bold outline-none">
          <option value="All">All Sources</option><option value="Enquiry Form">Enquiry Form</option><option value="App Registration">App Registration</option>
        </select>
        <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} className="w-full rounded-2xl border border-slate-200 px-5 py-3 text-sm font-bold outline-none">
          {uniqueCourses.map(course => <option key={course} value={course}>{course}</option>)}
        </select>
      </div>

      {userRole === "Super Admin" && selectedLeads.length > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-2xl bg-blue-50 px-6 py-4 border border-blue-100">
          <p className="text-sm font-black text-blue-800">{selectedLeads.length} Lead(s) Selected</p>
          <div className="flex gap-3">
            <button onClick={() => setSelectedLeads([])} className="rounded-xl px-4 py-2 text-xs font-bold text-blue-600">Cancel</button>
            <button onClick={() => setIsAssignModalOpen(true)} className="rounded-xl bg-blue-600 px-5 py-2 text-xs font-black text-white">Assign to Counselor</button>
          </div>
        </div>
      )}

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-sm font-semibold text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 border-b border-slate-100 text-xs font-black uppercase text-slate-500">
                <tr>
                  {userRole === "Super Admin" && (
                    <th className="py-4 pl-6 pr-3 w-10">
                      <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={filteredLeads.length > 0 && selectedLeads.length === filteredLeads.length} onChange={handleSelectAll} />
                    </th>
                  )}
                  <th className={`py-4 pr-4 ${userRole !== "Super Admin" ? "pl-6" : ""}`}>Student Info</th>
                  <th className="py-4 pr-4">Course Interest</th>
                  <th className="py-4 pr-4">Date Added</th>
                  <th className="py-4 pr-6">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-50">
                    {userRole === "Super Admin" && (
                      <td className="py-4 pl-6 pr-3">
                        <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={selectedLeads.includes(lead.id)} onChange={() => handleSelectLead(lead.id)} />
                      </td>
                    )}
                    <td className={`py-4 pr-4 ${userRole !== "Super Admin" ? "pl-6" : ""}`}>
                      <p className="font-black text-slate-950">{lead.name}</p>
                      <p className="text-[11px] font-bold text-slate-500">{lead.phone} • {lead.email}</p>
                      <span className="inline-block mt-1 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-black uppercase">{lead.source}</span>
                    </td>
                    <td className="py-4 font-bold text-slate-700">{lead.interest}</td>
                    <td className="py-4 text-xs font-semibold text-slate-500">{formatDate(lead.date)}</td>
                    <td className="py-4 pr-6">
                      {userRole === "Super Admin" ? (
                        <div className="flex flex-col items-start gap-1">
                          <span className={`px-2 py-1 text-[10px] rounded-md font-black uppercase ${lead.assignedTo ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                            {lead.assignedTo ? `Assigned: ${shortenEmail(lead.assignedTo)}` : "Unassigned"}
                          </span>
                          <button onClick={() => navigate(`/learn-with-us/lead-history/${lead.id}`)} className="text-[10px] font-bold text-blue-600 hover:underline mt-1">
                            View History
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { setCurrentLeadForRemark(lead); setIsRemarkModalOpen(true); }} className="rounded-xl bg-blue-50 px-4 py-2 text-[11px] font-black text-blue-600 border border-blue-100 hover:bg-blue-100">
                          + ADD REMARK
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ASSIGN MODAL (Admin) */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 relative">
            <button onClick={() => setIsAssignModalOpen(false)} className="absolute right-6 top-6 p-2 bg-slate-100 rounded-full"><X size={20}/></button>
            <h2 className="mb-2 text-2xl font-black">Assign Leads</h2>
            <select value={selectedCounselorEmail} onChange={(e) => setSelectedCounselorEmail(e.target.value)} className="w-full mb-6 mt-4 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold">
              <option value="">-- Select Counselor --</option>
              {counselors.map(c => <option key={c.id} value={c.email}>{c.name}</option>)}
            </select>
            <button onClick={handleAssignLeads} disabled={isAssigning} className="w-full bg-blue-600 text-white rounded-2xl py-3 font-black">{isAssigning ? "Assigning..." : "Confirm"}</button>
          </div>
        </div>
      )}

      {/* REMARK MODAL (Counselor) */}
      {isRemarkModalOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 relative">
            <button onClick={() => setIsRemarkModalOpen(false)} className="absolute right-6 top-6 p-2 bg-slate-100 rounded-full"><X size={20}/></button>
            <h2 className="mb-1 text-xl font-black">Add Remark</h2>
            <p className="text-xs font-bold text-slate-500 mb-4">Lead: {currentLeadForRemark?.name}</p>
            
            <label className="text-xs font-black uppercase text-slate-500">Call Status</label>
            <select value={remarkData.status} onChange={(e) => setRemarkData({...remarkData, status: e.target.value})} className="w-full mb-4 mt-1 rounded-xl border px-4 py-3 text-sm font-bold">
              <option value="Interested">Interested</option>
              <option value="Not Answered">Not Answered</option>
              <option value="Call Back Later">Call Back Later</option>
              <option value="Not Interested">Not Interested</option>
              <option value="Converted">Converted (Admission Done)</option>
            </select>

            <label className="text-xs font-black uppercase text-slate-500">Discussion Notes</label>
            <textarea 
              rows={4} value={remarkData.text} onChange={(e) => setRemarkData({...remarkData, text: e.target.value})} 
              placeholder="What did the student say?"
              className="w-full mb-6 mt-1 rounded-xl border px-4 py-3 text-sm font-semibold outline-none focus:border-blue-500"
            />
            
            <button onClick={handleAddRemark} disabled={isSubmittingRemark} className="w-full bg-slate-900 text-white rounded-xl py-3 font-black hover:bg-slate-800">
              {isSubmittingRemark ? "Saving..." : "Save Remark"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadEnquiry;